import React, { Fragment, lazy, useEffect, useState, useCallback } from "react";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { observer } from "mobx-react-lite";
import { ChevronRight, LocationOn } from "@mui/icons-material";
import { APIProvider } from "@vis.gl/react-google-maps";
import { usePageContext } from "@root/renderer/usePageContext";
import { useAccountContext } from "@root/stores/accountStore";
import { useEventPageContext } from "@root/stores/eventPageStore";
import { accountService } from "@root/services/api/accountService";
import { eventService } from "@root/services/api/eventService";
import { organizationService } from "@root/services/api/organizationService";
import { hasErrors, parseEventFromBackend } from "@root/utils/functions";
import { Event, Organization } from "@root/utils/entities";
import { StyledChip } from "@root/components/common/styled";
import { EventList } from "@root/components/EventList";
import { EventSearchFilters } from "@root/components/EventSearchFilters";
import { LoadingSpinner } from "@root/components/common/LoadingSpinner";
import { navigate } from "vike/client/router";
import { useMapSetup } from "@root/hooks/useMapSetup";
import { LoadingOverlay } from "@root/components/common/LoadingOverlay";

const Map = lazy(() =>
  import("./components/Map").then((module) => ({ default: module.Map })),
);
const ViewEventModal = lazy(() =>
  import("@root/components/modals/ViewEventModal").then((module) => ({
    default: module.ViewEventModal,
  })),
);
const ViewOrganizationModal = lazy(() =>
  import("@root/components/modals/ViewOrganizationModal").then((module) => ({
    default: module.ViewOrganizationModal,
  })),
);
const MobileEventView = lazy(() =>
  import("@root/components/MobileEventView").then((module) => ({
    default: module.MobileEventView,
  })),
);
const MobileOrganizationView = lazy(() =>
  import("@root/components/MobileOrganizationView").then((module) => ({
    default: module.MobileOrganizationView,
  })),
);

export const Page: React.FC = observer(() => {
  const pageContext = usePageContext();
  const accountStore = useAccountContext();
  const store = useEventPageContext();

  useMapSetup(store, accountStore);

  useEffect(() => {
    const handleTokenVerification = async (token: string) => {
      store.showLoading();
      try {
        const response = await accountService.verifyToken(token);
        if (response.data) {
          await accountStore.setAccount(response.data);
          if (pageContext.urlParsed.search.orgId) {
            await accountStore.switchToOrgProfile(
              pageContext.urlParsed.search.orgId,
            );
          }
          window.location.href = pageContext.urlParsed.search.redir || "/";
        }
      } catch (error) {
        console.error("Token verification error:", error);
      } finally {
        store.hideLoading();
      }
    };

    const loadEvent = async (id: string) => {
      store.showLoading();

      const response = await eventService.getEvent(id);
      if (!hasErrors(response) && response.data?.event) {
        const parsedEvent = parseEventFromBackend(response.data.event);
        store.showEvent(parsedEvent);
      }

      store.hideLoading();
    };

    const loadOrganization = async (id: string) => {
      store.showLoading();

      const response = await organizationService.getOrganization(id);

      if (hasErrors(response)) {
        await navigate("/", { overwriteLastHistoryEntry: true });
      } else {
        store.showOrganization(response.data!.organization);
      }

      store.hideLoading();
    };

    const { token, promptLogin, event, organization } =
      pageContext.urlParsed.search;

    if (token) handleTokenVerification(token);
    else if (promptLogin) {
      accountStore.authModalStore.openLoginModal(
        pageContext.urlParsed.search.redir || "/",
      );
    }

    if (event) loadEvent(event);
    if (organization) loadOrganization(organization);
  }, [pageContext, store, accountStore]);

  return (
    <APIProvider apiKey={import.meta.env.PUBLIC_ENV__GOOGLE_MAPS_API_KEY}>
      <EventPage />
    </APIProvider>
  );
});

const EventPage: React.FC = observer(() => {
  const eventPageStore = useEventPageContext();
  const theme = useTheme();
  const mediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!mounted) setMounted(true);
  }, [mounted]);

  if (!mounted) return null;

  return (
    <>
      <LoadingOverlay show={eventPageStore.isLoading} />
      {mediumScreen ? <MobileView /> : <WebView />}
    </>
  );
});

const clearOrgUrlParam = () => {
  const url = new URL(window.location.href);
  if (url.searchParams.has("organization")) {
    url.searchParams.delete("organization");
    window.history.replaceState({}, "", url.pathname + url.search);
  }
};

const WebView: React.FC = observer(() => {
  const eventPageStore = useEventPageContext();
  const restorePreviousFromOrganization = () => {
    clearOrgUrlParam();
    const previousEvent = eventPageStore.event;
    eventPageStore.hideOrganization();
    if (previousEvent) {
      eventPageStore.showEvent(previousEvent);
    }
  };

  return (
    <Fragment>
      <EventGrid />
      <React.Suspense fallback={<LoadingSpinner />}>
        {eventPageStore.organization &&
          (eventPageStore.currentSubview === "organization" ||
            eventPageStore.event) && (
            <ViewOrganizationModal
              store={eventPageStore.orgModalStore}
              organization={eventPageStore.organization}
              onClose={restorePreviousFromOrganization}
              onBack={restorePreviousFromOrganization}
              onViewEvent={(event: Event) => eventPageStore.showEvent(event)}
            />
          )}
        {eventPageStore.event &&
          (eventPageStore.currentSubview === "event" ||
            eventPageStore.organization) && (
            <ViewEventModal
              event={eventPageStore.event}
              store={eventPageStore.viewModalStore}
              onClose={() => eventPageStore.hideEvent()}
              viewOrganization={(organization: Organization) => {
                eventPageStore.showOrganization(organization);
                eventPageStore.viewModalStore.close();
              }}
              onFollowChange={(isFollowed) => {
                // Reflect follow/unfollow done in the detail view back in the list
                if (eventPageStore.event) {
                  eventPageStore.setEventFollowState(
                    eventPageStore.event.id,
                    isFollowed,
                  );
                }
              }}
              onHideChange={(eventId, isHidden) => {
                // If event was hidden, remove it from the list
                if (isHidden) {
                  eventPageStore.removeEvent(eventId);
                }
              }}
              onViewEvent={(event: Event) => eventPageStore.showEvent(event)}
            />
          )}
      </React.Suspense>
    </Fragment>
  );
});

const EventGrid: React.FC = observer(() => {
  const accountStore = useAccountContext();
  const eventPageStore = useEventPageContext();

  const onPageChange = useCallback(
    (newPage: number) => {
      // Ensure the page is set in the store before fetching
      // This ensures consistency even if the store update hasn't propagated yet
      if (eventPageStore.paginationStore.page !== newPage) {
        eventPageStore.paginationStore.setPage(newPage);
      }
      // Fetch the new page from the API
      eventPageStore.searchEvents(accountStore.userLocation);
    },
    [eventPageStore, accountStore.userLocation],
  );

  if (!eventPageStore.events) return null;

  return (
    <Grid container sx={{ height: "100%", flexWrap: "nowrap" }}>
      <Grid
        item
        xs={6}
        sx={{
          height: "100%",
          overflowY: "auto",
          padding: { xs: "0.5rem", sm: "1rem" },
          scrollBehavior: "smooth",
          minWidth: 0,
          width: "100%",
        }}
        id="event-list-scroll-container"
      >
        <EventSearchFilters />
        {eventPageStore.isSearching ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: { xs: "calc(100vh - 200px)", sm: "400px", md: "500px" },
              width: "100%",
            }}
          >
            <LoadingSpinner />
          </Box>
        ) : eventPageStore.events.length > 0 ? (
          <EventList
            events={eventPageStore.events}
            paginationStore={eventPageStore.paginationStore}
            onPageChange={onPageChange}
            onViewEvent={(event) => eventPageStore.showEvent(event)}
            onHideEvent={(eventId, isHidden) => {
              // Only remove event from list when it's hidden (not when unhidden)
              if (isHidden) {
                eventPageStore.removeEvent(eventId);
              }
            }}
          />
        ) : (
          <EmptyResults />
        )}
      </Grid>
      <Grid
        item
        xs={6}
        sx={{ border: "1px solid transparent", height: "100%" }}
      >
        {accountStore.locationRequested && (
          <React.Suspense fallback={<LoadingSpinner />}>
            <Map />
          </React.Suspense>
        )}
      </Grid>
    </Grid>
  );
});

const MobileView: React.FC = observer(() => {
  const accountStore = useAccountContext();
  const eventPageStore = useEventPageContext();

  const onPageChange = useCallback(
    (newPage: number) => {
      // Ensure the page is set in the store before fetching
      // This ensures consistency even if the store update hasn't propagated yet
      if (eventPageStore.paginationStore.page !== newPage) {
        eventPageStore.paginationStore.setPage(newPage);
      }
      // Fetch the new page from the API
      eventPageStore.searchEvents(accountStore.userLocation);
    },
    [eventPageStore, accountStore.userLocation],
  );

  return (
    <Fragment>
      {eventPageStore.organization &&
      (eventPageStore.currentSubview === "organization" ||
        eventPageStore.event) ? (
        <React.Suspense fallback={<LoadingSpinner />}>
          <MobileOrganizationView
            organization={eventPageStore.organization!}
            onClose={() => {
              clearOrgUrlParam();
              eventPageStore.hideOrganization();
            }}
            onBack={() => {
              clearOrgUrlParam();
              eventPageStore.hideOrganization();
            }}
            onViewOrganization={(organization) =>
              eventPageStore.showOrganization(organization)
            }
            onViewEvent={(event: Event) => eventPageStore.showEvent(event)}
          />
        </React.Suspense>
      ) : null}
      {eventPageStore.event &&
      (eventPageStore.currentSubview === "event" ||
        eventPageStore.organization) ? (
        <React.Suspense fallback={<LoadingSpinner />}>
          <MobileEventView
            event={eventPageStore.event!}
            onOrganizationClick={(organization) => {
              eventPageStore.showOrganization(organization);
              eventPageStore.hideEvent();
            }}
            onClose={() => eventPageStore.hideEvent()}
            onFollowChange={(isFollowed) => {
              // Reflect follow/unfollow done in the detail view back in the list
              if (eventPageStore.event) {
                eventPageStore.setEventFollowState(
                  eventPageStore.event.id,
                  isFollowed,
                );
              }
            }}
            onHideChange={(eventId, isHidden) => {
              // If event was hidden, remove it from the list
              if (isHidden) {
                eventPageStore.removeEvent(eventId);
              }
            }}
            onViewEvent={(event: Event) => eventPageStore.showEvent(event)}
          />
        </React.Suspense>
      ) : (
        <></>
      )}
      <Grid
        container
        sx={{
          padding: 0,
          display: eventPageStore.currentSubview ? "none" : "grid",
          gap: 0,
          flexGrow: 1,
          height: "100%",
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            height: "100%",
            overflowY: "auto",
            scrollBehavior: "smooth",
          }}
          id="event-list-scroll-container"
        >
          <Box
            sx={{
              padding: { xs: "1rem", sm: "1rem", md: "1rem" },
              paddingBottom: { xs: "3rem", sm: "3rem", md: "3rem" },
              display: eventPageStore.viewMode === "list" ? "flex" : "none",
              flexDirection: "column",
              width: "100%",
              minWidth: 0,
              minHeight: { xs: "100vh", sm: "auto" },
              overflow: "hidden",
            }}
          >
            {!eventPageStore.currentSubview && <EventSearchFilters />}
            {eventPageStore.isSearching ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: { xs: "calc(100vh - 300px)", sm: "400px", md: "500px" },
                  width: "100%",
                }}
              >
                <LoadingSpinner />
              </Box>
            ) : eventPageStore.events && eventPageStore.events!.length > 0 ? (
              <EventList
                events={eventPageStore.events ?? []}
                paginationStore={eventPageStore.paginationStore}
                onPageChange={onPageChange}
                onViewEvent={(event) => eventPageStore.showEvent(event)}
                onHideEvent={(eventId, isHidden) => {
                  // Only remove event from list when it's hidden (not when unhidden)
                  if (isHidden) {
                    eventPageStore.removeEvent(eventId);
                  }
                }}
              />
            ) : (
              <EmptyResults />
            )}
          </Box>
          {accountStore.locationRequested && (
            <React.Suspense>
              <Map />
            </React.Suspense>
          )}
          <MobileToggleButtons />
        </Grid>
      </Grid>
    </Fragment>
  );
});

const MobileToggleButtons = observer(() => {
  const eventPageStore = useEventPageContext();

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: "1rem",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
      }}
    >
      <StyledChip
        onClick={(e) => {
          e.preventDefault();
          if (eventPageStore.viewMode === "list") {
            eventPageStore.showMap();
          } else {
            eventPageStore.showList();
          }
        }}
        variant="outlined"
        label={eventPageStore.viewMode === "list" ? "View Map" : "View List"}
        icon={
          eventPageStore.viewMode === "list" ? (
            <LocationOn sx={{ width: "auto", height: "1.25rem" }} />
          ) : (
            <ChevronRight sx={{ width: "auto", height: "1.25rem" }} />
          )
        }
      />
    </Box>
  );
});

const EmptyResults: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      width: "100%",
      minWidth: 0,
      minHeight: { xs: "calc(100vh - 200px)", sm: "400px", md: "500px" },
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: { xs: "200px", sm: "300px" },
        width: "100%",
        padding: { xs: "2rem 1rem", sm: "3rem 2rem" },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: "#666",
          fontSize: { xs: "0.875rem", sm: "1rem" },
          textAlign: "center",
        }}
      >
        No events were found. Try adjusting your filters.
      </Typography>
    </Box>
  </Box>
);
